/**
 * Session-level cache helpers: skip API calls when Vuex already has fresh data.
 */

export const PREVIOUS_TRANSACTION_CACHE_MS = 20000;

export function createDefaultApiCache() {
  return {
    feedbackLoaded: false,
    exchangesListLoaded: false,
    stockExchangesKey: null,
    settingsLoaded: false,
    userInfoLoaded: false,
    marketInfoMerchantId: null,
    currenciesKey: null,
    rewardsOptInMerchantId: null,
    previousTransactionKey: null,
    previousTransactionResult: null,
    previousTransactionAt: 0,
  };
}

export function getCurrenciesCacheKey(cart) {
  if (!cart?.merchant_id) {
    return null;
  }
  return [cart.merchant_id, cart.amount ?? "", cart.currency || ""].join("|");
}

export function getPreviousTransactionCacheKey(cartOrPayload) {
  const merchantId = cartOrPayload?.merchant_id ?? cartOrPayload?.merchantId;
  if (!merchantId) {
    return null;
  }
  return [
    merchantId,
    cartOrPayload?.offerId || cartOrPayload?.order || "",
    cartOrPayload?.hostedPageId || cartOrPayload?.uuid || "",
  ].join("|");
}

export function getStockExchangesCacheKey(clientId, source = "default") {
  if (!clientId) {
    return null;
  }
  return `${clientId}|${source}`;
}

export function getCartSessionKey(cart) {
  if (!cart?.merchant_id) {
    return null;
  }
  return [
    cart.merchant_id,
    cart.order || "",
    cart.uuid || "",
    cart.amount ?? "",
    cart.currency || "",
  ].join("|");
}

export function hasMarketInfoForMerchant(marketInfo, merchantId) {
  if (!merchantId) {
    return false;
  }
  const loadedMerchantId =
    marketInfo?.merchant?.id ?? marketInfo?.merchant?.merchantId;
  return (
    Boolean(loadedMerchantId) &&
    String(loadedMerchantId) === String(merchantId) &&
    Object.keys(marketInfo?.settings || {}).length > 0
  );
}

export function hasCachedUserInfo(userInfo) {
  return Boolean(userInfo?.id || userInfo?.email);
}

export function hasCachedSettings(settings) {
  return Boolean(settings && Object.keys(settings).length);
}

export function hasCachedExchanges(stockExchanges) {
  return Boolean(
    stockExchanges &&
      (Array.isArray(stockExchanges)
        ? stockExchanges.length
        : Object.keys(stockExchanges).length)
  );
}

export function hasCachedExchangesCatalog(allStocks) {
  return Boolean(Array.isArray(allStocks) && allStocks.length);
}

export function shouldFetchMarketInfo(state, merchantId) {
  if (!merchantId) {
    return false;
  }
  if (
    state.apiCache?.marketInfoMerchantId === String(merchantId) &&
    hasMarketInfoForMerchant(state.marketInfo, merchantId)
  ) {
    return false;
  }
  return !hasMarketInfoForMerchant(state.marketInfo, merchantId);
}

export function shouldFetchUserInfo(state) {
  if (state.apiCache?.userInfoLoaded && hasCachedUserInfo(state.userInfo)) {
    return false;
  }
  return !hasCachedUserInfo(state.userInfo);
}

export function shouldFetchSettings(state) {
  if (state.apiCache?.settingsLoaded && hasCachedSettings(state.settings)) {
    return false;
  }
  return !hasCachedSettings(state.settings);
}

export function shouldFetchExchangesList(state) {
  if (state.apiCache?.exchangesListLoaded) {
    return false;
  }
  return !hasCachedExchangesCatalog(state.allStocks);
}

export function shouldFetchFeedback(state) {
  return !state.apiCache?.feedbackLoaded;
}

export function shouldFetchCurrencies(state, cart) {
  if (!cart?.merchant_id) {
    return false;
  }
  const key = getCurrenciesCacheKey(cart);
  if (!key) {
    return false;
  }
  return !(
    state.apiCache?.currenciesKey === key &&
    Array.isArray(state.currencies) &&
    state.currencies.length > 0
  );
}

export function shouldFetchRewardsOptIn(state, merchantId) {
  if (!merchantId) {
    return false;
  }
  if (
    state.apiCache?.rewardsOptInMerchantId === String(merchantId) &&
    state.enrolledInRewards &&
    typeof state.enrolledInRewards === "object" &&
    Object.keys(state.enrolledInRewards).length > 0
  ) {
    return false;
  }
  return true;
}

export function shouldFetchStockExchanges(state, clientId, source) {
  const key = getStockExchangesCacheKey(clientId, source);
  if (!key) {
    return true;
  }
  if (
    state.apiCache?.stockExchangesKey === key &&
    hasCachedExchanges(state.stockExchanges)
  ) {
    return false;
  }
  return true;
}

export function getCachedPreviousTransaction(state, cartOrPayload) {
  const key = getPreviousTransactionCacheKey(cartOrPayload);
  if (!key || state.apiCache?.previousTransactionKey !== key) {
    return null;
  }
  if (
    Date.now() - (state.apiCache?.previousTransactionAt || 0) >
    PREVIOUS_TRANSACTION_CACHE_MS
  ) {
    return null;
  }
  return state.apiCache?.previousTransactionResult ?? null;
}

export function invalidateSettingsCache(state) {
  state.apiCache.settingsLoaded = false;
}

export function invalidateCurrenciesCache(state, cart) {
  const currenciesKey = getCurrenciesCacheKey(cart);
  if (state.apiCache.currenciesKey !== currenciesKey) {
    state.apiCache.currenciesKey = null;
    // Keep state.currencies visible until a new fetch replaces it (avoids list flicker).
  }
}

export function invalidatePreviousTransactionCache(state, cart) {
  const txKey = getPreviousTransactionCacheKey(cart);
  if (state.apiCache.previousTransactionKey !== txKey) {
    state.apiCache.previousTransactionKey = null;
    state.apiCache.previousTransactionResult = null;
    state.apiCache.previousTransactionAt = 0;
  }
}

/** @deprecated Prefer invalidateCurrenciesCache / invalidatePreviousTransactionCache. */
export function invalidateCartDependentCache(state, cart) {
  invalidateCurrenciesCache(state, cart);
  invalidatePreviousTransactionCache(state, cart);
}
